import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  FilePlus2,
  ImagePlus,
  LoaderCircle,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { LandingPageLayout } from './LandingPage';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const MAX_IMAGE_FILES = 8;

const blankContent = {
  headline: '',
  subheadline: '',
  intro: '',
  problem_heading: '',
  problem_copy: '',
  pain_points: [],
  transformation_heading: '',
  transformation_copy: '',
  benefits: [],
  offer_heading: '',
  offer_copy: '',
  included: [],
  steps: [],
  faqs: [],
  closing_heading: '',
  closing_copy: '',
  cta_text: 'Explore the offer',
  cta_url: '',
  disclaimer: '',
  hero_asset_id: null,
};

const blankForm = {
  title: '',
  slug: '',
  source_url: '',
  source_text: '',
  audience_hint: '',
  cta_url: '',
  show_in_menu: false,
  show_on_profiles: true,
  practitioner_ids: [],
};

const readLines = (value) =>
  String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const responseError = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  return body.detail || fallback;
};

const AdminLandingPages = () => {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [content, setContent] = useState(blankContent);
  const [editingId, setEditingId] = useState(null);
  const [originalStatus, setOriginalStatus] = useState('draft');
  const [assets, setAssets] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [notice, setNotice] = useState({ type: '', text: '' });

  useEffect(
    () => () => {
      newFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    },
    [newFiles],
  );

  const selectedPractitioners = useMemo(
    () => practitioners.filter((item) => form.practitioner_ids.includes(item.id || item.ID)),
    [practitioners, form.practitioner_ids],
  );

  const loadPages = async () => {
    const response = await fetch(`${API_BASE}/api/admin/landing-pages`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error(await responseError(response, 'Could not load landing pages.'));
    setPages(await response.json());
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      setLoading(false);
      return;
    }
    let active = true;
    Promise.all([
      fetch(`${API_BASE}/api/admin/landing-pages`, { credentials: 'include' }),
      fetch(`${API_BASE}/api/admin/practitioners`, { credentials: 'include' }),
    ])
      .then(async ([pagesResponse, practitionersResponse]) => {
        if (!pagesResponse.ok) {
          throw new Error(await responseError(pagesResponse, 'Could not load landing pages.'));
        }
        if (!practitionersResponse.ok) {
          throw new Error(await responseError(practitionersResponse, 'Could not load practitioners.'));
        }
        const [pageData, practitionerData] = await Promise.all([
          pagesResponse.json(),
          practitionersResponse.json(),
        ]);
        if (active) {
          setPages(pageData);
          setPractitioners(practitionerData);
        }
      })
      .catch((error) => {
        if (active) setNotice({ type: 'error', text: error.message });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const closeEditor = () => {
    newFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setFormOpen(false);
    setEditorReady(false);
    setForm(blankForm);
    setContent(blankContent);
    setEditingId(null);
    setOriginalStatus('draft');
    setAssets([]);
    setNewFiles([]);
    setNotice({ type: '', text: '' });
  };

  const startNew = () => {
    closeEditor();
    setTimeout(() => setFormOpen(true), 0);
  };

  const updateForm = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));
  const updateContent = (key, value) => setContent((previous) => ({ ...previous, [key]: value }));

  const editPage = async (pageId) => {
    setNotice({ type: '', text: '' });
    try {
      const response = await fetch(`${API_BASE}/api/admin/landing-pages/${pageId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(await responseError(response, 'Could not open this page.'));
      const page = await response.json();
      setEditingId(page.id);
      setOriginalStatus(page.status || 'draft');
      setForm({
        title: page.title || '',
        slug: page.slug || '',
        source_url: page.source_url || '',
        source_text: page.source_text || '',
        audience_hint: page.audience_hint || '',
        cta_url: page.content?.cta_url || '',
        show_in_menu: Boolean(page.show_in_menu),
        show_on_profiles: page.show_on_profiles !== false,
        practitioner_ids: page.practitioner_ids || [],
      });
      setContent({ ...blankContent, ...(page.content || {}) });
      setAssets(page.assets || []);
      setNewFiles([]);
      setEditorReady(true);
      setFormOpen(true);
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    }
  };

  const togglePractitioner = (practitionerId) => {
    setForm((previous) => ({
      ...previous,
      practitioner_ids: previous.practitioner_ids.includes(practitionerId)
        ? previous.practitioner_ids.filter((id) => id !== practitionerId)
        : [...previous.practitioner_ids, practitionerId],
    }));
  };

  const handleFileSelection = (event) => {
    const selected = Array.from(event.target.files || []);
    const remaining = Math.max(0, MAX_IMAGE_FILES - assets.length - newFiles.length);
    const accepted = selected.slice(0, remaining).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNewFiles((previous) => [...previous, ...accepted]);
    if (selected.length > remaining) {
      setNotice({
        type: 'error',
        text: `A landing page can have up to ${MAX_IMAGE_FILES} graphics.`,
      });
    } else {
      setNotice({ type: '', text: '' });
    }
    event.target.value = '';
  };

  const generateCopy = async () => {
    setGenerating(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await fetch(`${API_BASE}/api/admin/landing-pages/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          source_text: form.source_text,
          source_url: form.source_url,
          audience_hint: form.audience_hint,
          cta_url: form.cta_url,
        }),
      });
      if (!response.ok) throw new Error(await responseError(response, 'Could not generate landing page copy.'));
      const result = await response.json();
      setContent({ ...blankContent, ...(result.content || {}) });
      setEditorReady(true);
      setNotice({
        type: 'success',
        text: 'Draft copy generated. Edit it below, then save it as a draft before publishing.',
      });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally {
      setGenerating(false);
    }
  };

  const savePage = async (status = 'draft') => {
    setSaving(true);
    setNotice({ type: '', text: '' });
    const initialStatus = status === 'published' && originalStatus !== 'published' ? 'draft' : status;
    let payload = {
      ...form,
      status: initialStatus,
      content: { ...content, cta_url: form.cta_url || content.cta_url || '' },
    };
    let uploaded = [];
    const uploadedFiles = [];
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_BASE}/api/admin/landing-pages/${editingId}`
        : `${API_BASE}/api/admin/landing-pages`;
      let response = await fetch(endpoint, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await responseError(response, 'Could not save this landing page.'));
      let saved = await response.json();
      setEditingId(saved.id);

      for (const item of newFiles) {
        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('alt_text', item.file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
        const uploadResponse = await fetch(
          `${API_BASE}/api/admin/landing-pages/${saved.id}/assets`,
          { method: 'POST', credentials: 'include', body: formData },
        );
        if (!uploadResponse.ok) {
          throw new Error(await responseError(uploadResponse, `Could not upload ${item.file.name}.`));
        }
        uploaded.push(await uploadResponse.json());
        uploadedFiles.push(item);
      }

      if (uploaded.length > 0 && !payload.content.hero_asset_id) {
        payload = {
          ...payload,
          content: { ...payload.content, hero_asset_id: uploaded[0].id },
        };
      }
      if (payload.status !== status || (uploaded.length > 0 && payload.content.hero_asset_id === uploaded[0].id)) {
        payload = { ...payload, status };
        response = await fetch(`${API_BASE}/api/admin/landing-pages/${saved.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(await responseError(response, 'The page saved, but its hero image could not be set.'));
        }
        saved = await response.json();
      }

      setContent(payload.content);
      setForm((previous) => ({ ...previous, slug: saved.slug }));
      setAssets(saved.assets || [...assets, ...uploaded]);
      setOriginalStatus(status);
      newFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      setNewFiles([]);
      await loadPages();
      window.dispatchEvent(new Event('landing-pages-updated'));
      setNotice({
        type: 'success',
        text: status === 'published'
          ? `Landing page published at /offers/${saved.slug}.`
          : 'Draft saved. It is not visible to the public yet.',
      });
    } catch (error) {
      if (uploaded.length > 0) {
        setAssets((previous) => {
          const ids = new Set(previous.map((asset) => asset.id));
          return [...previous, ...uploaded.filter((asset) => !ids.has(asset.id))];
        });
        uploadedFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setNewFiles((previous) => previous.filter((item) => !uploadedFiles.includes(item)));
      }
      setNotice({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (page) => {
    if (!window.confirm(`Permanently delete “${page.title}” and all its uploaded graphics? This cannot be undone.`)) {
      return;
    }
    setDeletingId(page.id);
    try {
      const response = await fetch(`${API_BASE}/api/admin/landing-pages/${page.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(await responseError(response, 'Could not delete this landing page.'));
      setPages((previous) => previous.filter((item) => item.id !== page.id));
      if (editingId === page.id) closeEditor();
      setNotice({ type: 'success', text: 'Landing page and its graphics were deleted.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally {
      setDeletingId(null);
    }
  };

  const deleteAsset = async (asset) => {
    if (!editingId) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/landing-pages/${editingId}/assets/${asset.id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!response.ok) throw new Error(await responseError(response, 'Could not delete this graphic.'));
      setAssets((previous) => previous.filter((item) => item.id !== asset.id));
      if (content.hero_asset_id === asset.id) updateContent('hero_asset_id', null);
      setNotice({ type: 'success', text: 'Graphic deleted.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    }
  };

  const onListText = (key, value) => updateContent(key, readLines(value));

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white px-6 pt-32 text-center">
        <h1 className="font-title text-3xl font-bold text-purple-900">Admin access required</h1>
        <p className="mt-3 text-gray-600">Sign in with an administrator account to manage landing pages.</p>
      </div>
    );
  }

  const heroPreviewUrl = content.hero_asset_id
    ? assets.find((asset) => asset.id === content.hero_asset_id)?.url || ''
    : newFiles[0]?.previewUrl || '';
  const previewPage = {
    title: form.title || 'Your offer',
    content: { ...content, cta_url: form.cta_url || content.cta_url },
    assets,
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 text-gray-900">
      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-6 sm:px-6 lg:px-8">
          <div>
            <Link to="/admin-dashboard" className="mb-2 inline-flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900">
              <ArrowLeft className="h-4 w-4" /> Admin dashboard
            </Link>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-purple-500">Admin workspace</p>
            <h1 className="font-title text-3xl font-bold text-purple-950">Landing page generator</h1>
            <p className="mt-1 text-sm text-gray-600">
              Generate offer copy, edit every section, then publish when it’s ready.
            </p>
          </div>
          {!formOpen && (
            <Button onClick={startNew} className="bg-purple-700 text-white hover:bg-purple-800">
              <FilePlus2 className="mr-2 h-4 w-4" /> Create a landing page
            </Button>
          )}
        </div>
      </div>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {notice.text && (
          <div
            role={notice.type === 'error' ? 'alert' : 'status'}
            className={`rounded-lg border px-4 py-3 ${
              notice.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-green-200 bg-green-50 text-green-800'
            }`}
          >
            {notice.text}
          </div>
        )}

        {formOpen ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{editingId ? 'Edit landing page' : 'Start a landing page'}</CardTitle>
                <p className="text-sm text-gray-600">
                  Add offer details or a public URL. Generated copy is saved only after you review it.
                </p>
              </CardHeader>
              <CardContent className="grid gap-5 md:grid-cols-2">
                <label className="space-y-1.5 text-sm font-medium">
                  Offer name
                  <Input
                    required
                    maxLength={180}
                    value={form.title}
                    onChange={(event) => updateForm('title', event.target.value)}
                    placeholder="For example: The 1:1 Clarity Session"
                  />
                </label>
                <label className="space-y-1.5 text-sm font-medium">
                  Page address
                  <div className="flex items-center overflow-hidden rounded-md border bg-gray-50">
                    <span className="px-3 text-sm text-gray-500">/offers/</span>
                    <Input
                      className="border-0 bg-transparent"
                      value={form.slug}
                      onChange={(event) => updateForm('slug', event.target.value)}
                      placeholder="generated-from-offer-name"
                    />
                  </div>
                </label>
                <label className="space-y-1.5 text-sm font-medium md:col-span-2">
                  Offer information
                  <Textarea
                    rows={6}
                    maxLength={16000}
                    value={form.source_text}
                    onChange={(event) => updateForm('source_text', event.target.value)}
                    placeholder="Paste the offer details, format, price, what is included, who it is for, and any confirmed logistics."
                  />
                </label>
                <label className="space-y-1.5 text-sm font-medium">
                  Or source URL
                  <Input
                    type="url"
                    maxLength={2000}
                    value={form.source_url}
                    onChange={(event) => updateForm('source_url', event.target.value)}
                    placeholder="https://example.com/offer"
                  />
                </label>
                <label className="space-y-1.5 text-sm font-medium">
                  Ideal audience (optional)
                  <Input
                    maxLength={2000}
                    value={form.audience_hint}
                    onChange={(event) => updateForm('audience_hint', event.target.value)}
                    placeholder="Who is this most helpful for?"
                  />
                </label>
                <label className="space-y-1.5 text-sm font-medium md:col-span-2">
                  Call-to-action destination
                  <Input
                    maxLength={2000}
                    value={form.cta_url}
                    onChange={(event) => {
                      updateForm('cta_url', event.target.value);
                      updateContent('cta_url', event.target.value);
                    }}
                    placeholder="https://checkout.example.com or /contact"
                  />
                  <span className="block text-xs font-normal text-gray-500">
                    Use a booking, checkout, or information page. A destination is required to publish.
                  </span>
                </label>
                <div className="space-y-2 md:col-span-2">
                  <p className="text-sm font-medium">Upload graphics</p>
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-purple-300 bg-purple-50/60 p-4 text-sm text-purple-900 hover:bg-purple-50">
                    <ImagePlus className="h-5 w-5" />
                    <span>Choose up to {MAX_IMAGE_FILES - assets.length - newFiles.length} more JPG, PNG, GIF, or WebP images (5 MB each)</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      multiple
                      className="sr-only"
                      onChange={handleFileSelection}
                    />
                  </label>
                  {(assets.length > 0 || newFiles.length > 0) && (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {assets.map((asset) => (
                        <div key={asset.id} className="relative overflow-hidden rounded-lg border bg-white">
                          <img src={asset.url} alt={asset.alt_text || ''} className="h-32 w-full object-cover" />
                          <div className="flex items-center justify-between gap-2 p-2 text-xs">
                            <span className="truncate">{asset.filename}</span>
                            <button type="button" onClick={() => deleteAsset(asset)} aria-label={`Delete ${asset.filename}`} className="text-red-600 hover:text-red-800">
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {content.hero_asset_id === asset.id && (
                            <span className="absolute left-2 top-2 rounded bg-purple-800 px-2 py-1 text-xs text-white">Hero image</span>
                          )}
                          {content.hero_asset_id !== asset.id && (
                            <button
                              type="button"
                              onClick={() => updateContent('hero_asset_id', asset.id)}
                              className="absolute right-2 top-2 rounded bg-white/90 px-2 py-1 text-xs font-medium text-purple-800"
                            >
                              Use as hero
                            </button>
                          )}
                        </div>
                      ))}
                      {newFiles.map((item, index) => (
                        <div key={`${item.file.name}-${index}`} className="relative overflow-hidden rounded-lg border bg-white">
                          <img src={item.previewUrl} alt="" className="h-32 w-full object-cover" />
                          <div className="flex items-center justify-between gap-2 p-2 text-xs">
                            <span className="truncate">{item.file.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                URL.revokeObjectURL(item.previewUrl);
                                setNewFiles((previous) => previous.filter((file) => file !== item));
                              }}
                              aria-label={`Remove ${item.file.name}`}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {index === 0 && assets.length === 0 && !content.hero_asset_id && (
                            <span className="absolute left-2 top-2 rounded bg-purple-800 px-2 py-1 text-xs text-white">First image becomes hero</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 md:col-span-2">
                  <Button
                    onClick={generateCopy}
                    disabled={generating || !form.title || (!form.source_text.trim() && !form.source_url.trim())}
                    className="bg-purple-700 text-white hover:bg-purple-800"
                  >
                    {generating ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    {generating ? 'Generating copy…' : 'Generate landing page'}
                  </Button>
                  {!editingId && !content.headline && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setContent({ ...blankContent });
                      setEditorReady(true);
                    }}
                    disabled={!form.title}
                  >
                      <Plus className="mr-2 h-4 w-4" /> Start with a blank draft
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {editorReady && (
              <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Edit page copy</CardTitle>
                      <p className="text-sm text-gray-600">Review every statement and link before publishing.</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <label className="block space-y-1.5 text-sm font-medium">
                        Main headline
                        <Input value={content.headline} onChange={(event) => updateContent('headline', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Subheadline
                        <Textarea rows={2} value={content.subheadline} onChange={(event) => updateContent('subheadline', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Introduction
                        <Textarea rows={3} value={content.intro} onChange={(event) => updateContent('intro', event.target.value)} />
                      </label>
                      <div className="grid gap-4 md:grid-cols-2">
                        <label className="space-y-1.5 text-sm font-medium">
                          Problem section heading
                          <Input value={content.problem_heading} onChange={(event) => updateContent('problem_heading', event.target.value)} />
                        </label>
                        <label className="space-y-1.5 text-sm font-medium">
                          Transformation heading
                          <Input value={content.transformation_heading} onChange={(event) => updateContent('transformation_heading', event.target.value)} />
                        </label>
                      </div>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Problem and empathy copy
                        <Textarea rows={3} value={content.problem_copy} onChange={(event) => updateContent('problem_copy', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Pain points (one per line)
                        <Textarea rows={4} value={(content.pain_points || []).join('\n')} onChange={(event) => onListText('pain_points', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Transformation copy
                        <Textarea rows={3} value={content.transformation_copy} onChange={(event) => updateContent('transformation_copy', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Benefits (one per line)
                        <Textarea rows={4} value={(content.benefits || []).join('\n')} onChange={(event) => onListText('benefits', event.target.value)} />
                      </label>
                      <div className="grid gap-4 md:grid-cols-2">
                        <label className="space-y-1.5 text-sm font-medium">
                          Offer section heading
                          <Input value={content.offer_heading} onChange={(event) => updateContent('offer_heading', event.target.value)} />
                        </label>
                        <label className="space-y-1.5 text-sm font-medium">
                          Closing heading
                          <Input value={content.closing_heading} onChange={(event) => updateContent('closing_heading', event.target.value)} />
                        </label>
                      </div>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Offer details
                        <Textarea rows={4} value={content.offer_copy} onChange={(event) => updateContent('offer_copy', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        What’s included (one per line)
                        <Textarea rows={4} value={(content.included || []).join('\n')} onChange={(event) => onListText('included', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        How it works (one step per line)
                        <Textarea rows={4} value={(content.steps || []).join('\n')} onChange={(event) => onListText('steps', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Closing copy
                        <Textarea rows={3} value={content.closing_copy} onChange={(event) => updateContent('closing_copy', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        CTA button text
                        <Input value={content.cta_text} onChange={(event) => updateContent('cta_text', event.target.value)} />
                      </label>
                      <label className="block space-y-1.5 text-sm font-medium">
                        Results disclaimer (optional)
                        <Textarea rows={2} value={content.disclaimer} onChange={(event) => updateContent('disclaimer', event.target.value)} />
                      </label>

                      <div className="space-y-3 border-t pt-4">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="font-semibold text-gray-900">Frequently asked questions</h3>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => updateContent('faqs', [...(content.faqs || []), { question: '', answer: '' }])}
                          >
                            <Plus className="mr-1 h-4 w-4" /> Add question
                          </Button>
                        </div>
                        {(content.faqs || []).map((faq, index) => (
                          <div key={`faq-${index}`} className="space-y-2 rounded-lg border p-3">
                            <div className="flex gap-2">
                              <Input
                                value={faq.question}
                                aria-label={`Question ${index + 1}`}
                                onChange={(event) => {
                                  const next = [...content.faqs];
                                  next[index] = { ...faq, question: event.target.value };
                                  updateContent('faqs', next);
                                }}
                                placeholder="Question"
                              />
                              <button
                                type="button"
                                onClick={() => updateContent('faqs', content.faqs.filter((_, itemIndex) => itemIndex !== index))}
                                aria-label={`Remove question ${index + 1}`}
                                className="rounded-md p-2 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            <Textarea
                              rows={2}
                              value={faq.answer}
                              aria-label={`Answer ${index + 1}`}
                              onChange={(event) => {
                                const next = [...content.faqs];
                                next[index] = { ...faq, answer: event.target.value };
                                updateContent('faqs', next);
                              }}
                              placeholder="Answer"
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle>Publishing and links</CardTitle></CardHeader>
                    <CardContent className="space-y-5">
                      <label className="flex items-start gap-3 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1 accent-purple-700"
                          checked={form.show_in_menu}
                          onChange={(event) => updateForm('show_in_menu', event.target.checked)}
                        />
                        <span><strong>Show in the main site menu</strong><br /><span className="text-gray-600">Adds this published page to the “More” menu on desktop and mobile.</span></span>
                      </label>
                      <label className="flex items-start gap-3 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1 accent-purple-700"
                          checked={form.show_on_profiles}
                          onChange={(event) => updateForm('show_on_profiles', event.target.checked)}
                        />
                        <span><strong>Show on selected practitioner profiles</strong><br /><span className="text-gray-600">Published offers are linked from the profiles selected below.</span></span>
                      </label>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Link practitioners to this offer</p>
                        {practitioners.length === 0 ? (
                          <p className="text-sm text-gray-500">No practitioner profiles are available yet.</p>
                        ) : (
                          <div className="grid gap-2 sm:grid-cols-2">
                            {practitioners.map((practitioner) => {
                              const id = practitioner.id || practitioner.ID;
                              return (
                                <label key={id} className="flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm">
                                  <input
                                    type="checkbox"
                                    className="accent-purple-700"
                                    checked={form.practitioner_ids.includes(id)}
                                    onChange={() => togglePractitioner(id)}
                                  />
                                  <span>{practitioner.Name}</span>
                                  {practitioner.published === false && <span className="text-xs text-gray-500">(unpublished)</span>}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {selectedPractitioners.length > 0 && (
                        <p className="text-xs text-gray-600">
                          Linked to: {selectedPractitioners.map((item) => item.Name).join(', ')}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => savePage('draft')} disabled={saving || !form.title} variant="outline">
                      {saving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                      Save draft
                    </Button>
                    <Button
                      onClick={() => savePage('published')}
                      disabled={saving || !form.title || !content.headline || !form.cta_url}
                      className="bg-purple-700 text-white hover:bg-purple-800"
                    >
                      {saving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                      Publish page
                    </Button>
                    <Button variant="ghost" onClick={closeEditor} disabled={saving}>Close</Button>
                    {editingId && form.slug && (
                      <a
                        href={`/offers/${form.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50"
                      >
                        <Eye className="h-4 w-4" /> Open public page
                      </a>
                    )}
                  </div>
                </div>

                <div className="space-y-3 xl:sticky xl:top-24">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">Page preview</h2>
                    <span className="text-xs text-gray-500">Preview only — save to access the public URL</span>
                  </div>
                  <div className="max-h-[78vh] overflow-y-auto rounded-xl border bg-white shadow-sm">
                    <LandingPageLayout page={previewPage} heroImageOverride={heroPreviewUrl} />
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Your landing pages</CardTitle>
                  <p className="mt-1 text-sm text-gray-600">Draft pages stay private until you publish them.</p>
                </div>
                <Button onClick={startNew} className="bg-purple-700 text-white hover:bg-purple-800">
                  <FilePlus2 className="mr-2 h-4 w-4" /> New page
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-gray-600">Loading landing pages…</p>
              ) : pages.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center">
                  <Sparkles className="mx-auto h-8 w-8 text-purple-500" />
                  <p className="mt-3 font-semibold">No landing pages yet</p>
                  <p className="mt-1 text-sm text-gray-600">Create an editable page from offer details or a source URL.</p>
                  <Button onClick={startNew} className="mt-4 bg-purple-700 text-white hover:bg-purple-800">
                    Create your first page
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {pages.map((page) => (
                    <article key={page.id} className="flex flex-col gap-4 rounded-xl border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="truncate font-semibold text-gray-900">{page.title}</h2>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            page.status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {page.status === 'published' ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm text-gray-500">/offers/{page.slug}</p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                          {page.show_in_menu && <span className="rounded bg-purple-50 px-2 py-1">Main menu</span>}
                          {page.show_on_profiles && <span className="rounded bg-purple-50 px-2 py-1">Practitioner profiles</span>}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {page.status === 'published' ? (
                          <a href={`/offers/${page.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-gray-50">
                            <Eye className="h-4 w-4" /> View
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-500">
                            <EyeOff className="h-4 w-4" /> Private
                          </span>
                        )}
                        <Button size="sm" variant="outline" onClick={() => editPage(page.id)}>Edit</Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-700"
                          onClick={() => deletePage(page)}
                          disabled={deletingId === page.id}
                        >
                          <Trash2 className="mr-1 h-4 w-4" /> Delete
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminLandingPages;
